import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import classNames from 'classnames';
import css from './CustomerDetails.module.scss';
import clone from 'just-clone';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { Country, PhoneType, DMSType, Events } from '../../../../API/Enums';
import { IAppointment, ContactPreference, insertAuditTrail } from '../../../../API/GoMoto';
import {
    useAppointmentState,
    useAppointmentDispatch,
    useAppointmentUpdateAndPersist,
    useMaskPhone,
    useMaskEmail,
    useTexting,
    OptInStatus,
    TextOptInStatus,
    ContactPreferenceEnum,
    AppointmentDispatchTypes,
} from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import {
    useLocalization,
    useLocalizationExtension,
} from '../../../../context/Localization/localization';
import { LocalizationType } from '../../../../context/Localization/localization.types';
import { GMAppointmentName } from '../../../../controls/AppointmentName';
import { CTAButton, GMContactPreferenceButton } from '../../../../controls/ButtonBase';
import { GMFormCheckbox } from '../../../../controls/Checkbox';
import { GMDialogInfo } from '../../../../controls/Dialog';
import { GMForm, GMFormWrapper, GMFormControl, GMErrorMessage } from '../../../../controls/Form';
import { GMGrid } from '../../../../controls/Grid';
import { Heading } from '../../../../controls/Heading/Heading';
import { LegalDocumentLinks } from '../../../../controls/LegalDocumentLinks/LegalDocumentLinks';
import { KeyboardLayouts } from '../../../../controls/SimpleKeyboard/Layout';
import { Spacer } from '../../../../controls/Spacer/Spacer';
import { GMFormEmailField, GMFormCustomerDetailsPhoneField } from '../../../../controls/TextField';
import { nameof } from '../../../../helpers/nameof';
import { formatAsPhoneNumber } from '../../../../helpers/Number';
import { isElectron } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { CustomerDetailsForm, CustomerDetailsProps } from './CustomerDetails.types';

/**
 * The contact preference component for check-in and walk-in appointments. It contains all of the
 * opt-in and opt-out logic for receiving text messages about a customer's appointment.
 *
 * @param onProceed - {@link CustomerDetailsProps.onProceed onProceed} description.
 */
const CustomerDetails: GoMotoComponent<CustomerDetailsProps> = ({
    onProceed,
}) => {
    // Localization Hooks
    const Localization: LocalizationType = useLocalization();
    const { formatRequired, emailInvalid } = useLocalizationExtension();

    // Kiosk Hooks
    const {
        dealer: {
            customerTextingActive,
            customerTextingEnabled,
            key: dealerKey,
            dmsType,
            name,
            textingPhoneUS,
            textingPhoneCA,
            country,
        },
    } = useKioskState();

    // Appointment Hooks
    const appointment: IAppointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const setAppointment = useAppointmentUpdateAndPersist();
    const maskPhone: boolean = useMaskPhone();
    const maskEmail: boolean = useMaskEmail();
    const { checkPhoneTextingStatus, updatePhoneOptInStatus } = useTexting();

    // Variables
    const isKiosk: boolean = isElectron();
    const textingPhone: string | undefined = country === Country.Canada ? textingPhoneCA : textingPhoneUS;
    const [inputedPhone, setInputedPhone] = useState<boolean>(false);
    const [inputedEmail, setInputedEmail] = useState<boolean>(false);
    const [optInStatus, setOptInStatus] = useState<OptInStatus | undefined>(undefined);
    const [showOptOutDialog, setShowOptOutDialog] = useState<boolean>(false);
    const [showNotDeliverableDialog, setShowNotDeliverableDialog] = useState<boolean>(false);
    const [hasClickedToProceed, setHasClickedToProceed] = useState<boolean>(false);

    // Formik Initialization
    /**
     * Determines the other phone type that the checkbox will alternate between. Checked will mean that the phoneType is 'Cell',
     * but unchecked will dynamically mean one of the other non-Cell phoneTypes available.
     */
    const nonCellType: PhoneType.home | PhoneType.work | PhoneType.other = (appointment.phoneTypeEnum && appointment.phoneTypeEnum === PhoneType.cell)
        || (!appointment.phoneTypeEnum) ? PhoneType.home : appointment.phoneTypeEnum;

    const initialValues: CustomerDetailsForm = {
        phone: appointment.phone,
        email: appointment.email ?? '',
        phoneTypeEnum: appointment.phoneTypeEnum ?? PhoneType.cell,
        lastName: appointment.lastName,
        textOptInStatus: optInStatus !== undefined ? optInStatus.status : TextOptInStatus.OptedIn,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object()
                .shape({
                    [nameof<CustomerDetailsForm>('email')]: Yup.string()
                        .trim()
                        .email(emailInvalid)
                        .nullable(),
                    [nameof<CustomerDetailsForm>('phone')]: Yup.string()
                        .trim()
                        .nullable(),
                    [nameof<CustomerDetailsForm>('contactPreference')]: Yup.string()
                        .oneOf(
                            [ContactPreferenceEnum.Email, ContactPreferenceEnum.Phone, ContactPreferenceEnum.Text],
                            formatRequired(Localization.ContactPreference),
                        )
                        .required(formatRequired(Localization.ContactPreference))
                        .nullable(),
                    [nameof<CustomerDetailsForm>('textOptInStatus')]: !customerTextingActive
                        || (customerTextingActive && optInStatus && optInStatus.isDeliverable === false)
                        || optInStatus === undefined ?
                        (
                            Yup.number()
                                .nullable()
                        ) :
                        (
                            Yup.number()
                                .when(nameof<CustomerDetailsForm>('contactPreference'), {
                                    is: (value: ContactPreference) => value === ContactPreferenceEnum.Text || (value === undefined && hasClickedToProceed),
                                    then: (schema) => schema.test('text-opt-in', (value) => {
                                        if (value !== TextOptInStatus.NotDecided) {
                                            return true;
                                        }
                                        else {
                                            return false;
                                        }
                                    }),
                                    otherwise: (schema) => schema.notRequired(),
                                })
                        ),

                }, [
                    [nameof<CustomerDetailsForm>('contactPreference'), nameof<CustomerDetailsForm>('textOptInStatus')],
                ])}
            onSubmit={async (values: CustomerDetailsForm) => {
                const originalAppointment: IAppointment = clone(appointment);
                // blank customer key -> new customer
                const isNewCustomer: boolean = !appointment.dmsCustomerKey;
                const isNewCustomerAppointment: boolean = !appointment.key;
                const wasEmailUpdated: boolean = initialValues.email.toLowerCase() !== values.email.toLowerCase() || isNewCustomer;
                const wasPhoneUpdated: boolean = initialValues.phone !== values.phone || isNewCustomer;
                const wasPhoneEnumUpdated: boolean = initialValues.phoneTypeEnum !== values.phoneTypeEnum;
                const wasContactPreferenceUpdated: boolean = originalAppointment.contactPreference !== values.contactPreference;
                const wasCustomerInfoUpdated: boolean = appointment.customerInfoUpdated
                    || wasEmailUpdated
                    || wasPhoneUpdated
                    || wasPhoneEnumUpdated
                    || wasContactPreferenceUpdated
                    || dmsType === DMSType.CDKEDGE;
                const wasTextOptedIn: boolean = (initialValues.textOptInStatus === TextOptInStatus.NotDecided || initialValues.textOptInStatus === TextOptInStatus.OptedOut)
                    && values.textOptInStatus === TextOptInStatus.OptedIn;

                if (wasTextOptedIn && !isNewCustomerAppointment) {
                    updatePhoneOptInStatus(values.phone, true)
                        .then(() => {
                            insertAuditTrail(appointment.key, Events.Text_Opt_In);
                        });
                }

                if (inputedPhone) {
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.UnmaskField,
                        payload: 'phone',
                    });
                }
                if (inputedEmail) {
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.UnmaskField,
                        payload: 'email',
                    });
                }

                const updatedAppointment: IAppointment = {
                    ...appointment,
                    ...values,
                    email: values.email.trim(),
                    dealerKey,
                    emailUpdated: wasEmailUpdated,
                    phoneUpdated: wasPhoneUpdated,
                    customerInfoUpdated: wasCustomerInfoUpdated,
                    optedIntoTextingStatus: values.textOptInStatus,
                    ...(wasEmailUpdated && {
                        oldEmail: initialValues.email.toLowerCase(),
                    }),
                    ...(wasPhoneUpdated && {
                        oldPhone: initialValues.phone,
                    }),
                    ...(wasPhoneEnumUpdated && {
                        oldPhoneTypeEnum: initialValues.phoneTypeEnum,
                    }),
                    ...(wasTextOptedIn && {
                        optedIntoTextingThisAppointment: wasTextOptedIn,
                        optInTime: new Date(Date.now()),
                    }),
                };
                // if newCustomerEnabled there will be another setAppointment call on ConfirmInformation, no need to do the customer update here
                return setAppointment(updatedAppointment, !isNewCustomer, isNewCustomer ? undefined : wasCustomerInfoUpdated)
                    .then(onProceed);
            }}
        >
            {({ values, setFieldValue, submitForm, errors, submitCount, resetForm }) => {
                return (
                    <GMForm
                        heading={
                            <Heading
                                primaryHeading={Localization.formatString(Localization.HiCustomer, <GMAppointmentName />)}
                                secondaryHeading={Localization.PleaseConfirmPhoneNumberEmail}
                                leftAlign
                            />
                        }
                        submitButton={
                            <CTAButton
                                styleType={'primary'}
                                title={Localization.ClickToProceedToNextStep}
                                label={Localization.Proceed}
                                id={Localization.ClickToProceedToNextStep}
                                ariaLabel={Localization.ClickToProceedToNextStep}
                                onClick={async () => {
                                    if (values.contactPreference !== ContactPreferenceEnum.Text || !customerTextingActive) {
                                        submitForm();
                                    }
                                    else {
                                        if (customerTextingActive && optInStatus && optInStatus.isDeliverable === false) {
                                            setShowNotDeliverableDialog(true);
                                        }
                                        else if (
                                            values.textOptInStatus === TextOptInStatus.NotDecided
                                            && optInStatus
                                            && optInStatus.isDeliverable !== false
                                            && customerTextingActive
                                        ) {
                                            setHasClickedToProceed(true);

                                            resetForm({
                                                values: {
                                                    ...values,
                                                    contactPreference: undefined,
                                                },
                                                errors: {
                                                    textOptInStatus: errors.textOptInStatus ? errors.textOptInStatus + 1 : 'textOptInStatus is invalid',
                                                },
                                            });
                                        }
                                        else if (
                                            values.textOptInStatus === TextOptInStatus.OptedOut
                                            && optInStatus
                                            && optInStatus.isDeliverable !== false
                                            && customerTextingActive
                                        ) {
                                            const { data } = await checkPhoneTextingStatus(optInStatus.phone);

                                            await setFieldValue(nameof<CustomerDetailsForm>('textOptInStatus'), data.status);
                                            setOptInStatus(data);

                                            if (data.isDeliverable !== false && data.status === TextOptInStatus.OptedOut) {
                                                setShowOptOutDialog(true);
                                            }
                                            else {
                                                submitForm();
                                            }
                                        }
                                        else {
                                            submitForm();
                                        }
                                    }
                                }}
                            />
                        }
                        fullWidthKioskInputs
                        topDivider
                    >
                        <GMGrid
                            id={'customer-details-phone-email-list-container'}
                            container
                            className={css.phoneEmailList}
                            rowGap={1}
                        >
                            <GMGrid
                                id={'customer-details-required-fields-container'}
                                container
                                xs={12}
                            >
                                <GMGrid
                                    id={'customer-details-required-fields-grid'}
                                    xs={12}
                                    md={7}
                                >
                                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                                </GMGrid>
                            </GMGrid>
                            <GMGrid
                                id={'customer-details-phone-container'}
                                container
                                wrap={'nowrap'}
                                gap={isKiosk ? 0 : 1.5}
                                xs={12}
                            >
                                <GMGrid id={'customer-details-phone-grid'} xs={7}>
                                    <GMFormCustomerDetailsPhoneField
                                        name={nameof<CustomerDetailsForm>('phone')}
                                        id={nameof<CustomerDetailsForm>('phone')}
                                        title={Localization.PhoneNumber}
                                        label={Localization.PhoneNumber}
                                        ariaLabel={Localization.PhoneNumber}
                                        initiallyMasked={maskPhone}
                                        onChangeCallback={() => setInputedPhone(true)}
                                        autoFocus={initialValues.phone === ''}
                                        optInStatusFieldName={nameof<CustomerDetailsForm>('textOptInStatus')}
                                        optInStatus={optInStatus}
                                        setOptInStatus={setOptInStatus}
                                        textingActive={customerTextingActive}
                                        required
                                    />
                                </GMGrid>
                                <GMGrid id={'customer-details-phone-checkbox-grid'} xs={5}>
                                    <GMFormCheckbox
                                        label={Localization.ThisIsMyCellPhoneNumber}
                                        labelPlacement={'start'}
                                        labelVariant={'heading'}
                                        extraLabelWidth={true}
                                        checked={values.phoneTypeEnum === PhoneType.cell}
                                        onChange={() => {
                                            setFieldValue(nameof<CustomerDetailsForm>('phoneTypeEnum'), values.phoneTypeEnum === PhoneType.cell ? nonCellType : PhoneType.cell);
                                        }}
                                    />
                                </GMGrid>
                            </GMGrid>
                            <GMGrid
                                id={'customer-details-email-container'}
                                container
                                wrap={'nowrap'}
                                gap={isKiosk ? 0 : 1.5}
                                xs={12}
                            >
                                <GMGrid id={'customer-details-email-grid'} xs={7}>
                                    <GMFormEmailField
                                        name={nameof<CustomerDetailsForm>('email')}
                                        id={nameof<CustomerDetailsForm>('email')}
                                        title={Localization.EmailAddress}
                                        label={Localization.EmailAddress}
                                        ariaLabel={Localization.EmailAddress}
                                        initiallyMasked={maskEmail}
                                        onChangeCallback={() => setInputedEmail(true)}
                                        autoFocus={initialValues.phone !== '' && initialValues.email === ''}
                                        keyboardLayout={KeyboardLayouts.Email}
                                        required
                                    />
                                </GMGrid>
                                <GMGrid id={'customer-details-email-right-offset-grid'} xs={isKiosk ? 0 : 5} />
                            </GMGrid>
                        </GMGrid>
                        <Spacer />
                        <Heading secondaryHeading={Localization.HowMayWeContactYou + '\u00A0*'} />
                        {customerTextingActive && optInStatus && optInStatus.isDeliverable !== false && optInStatus.status === TextOptInStatus.NotDecided ? (
                            <GMGrid
                                id={'customer-details-opt-in-container'}
                                container
                                className={css.textOptIn}
                            >
                                <GMGrid
                                    id={'customer-details-opt-in-grid'}
                                    xs={8}
                                    xsOffset={2}
                                >
                                    <GMFormControl className={css.textOptInError} error={!!errors.textOptInStatus}>
                                        <GMFormCheckbox
                                            key={Localization.ByCheckingThisBoxYouAgreeToReceiveTextMessages + errors.textOptInStatus}
                                            label={Localization.formatString(Localization.ByCheckingThisBoxYouAgreeToReceiveTextMessages, name) as string}
                                            labelPlacement={'end'}
                                            labelVariant={'none'}
                                            checked={values.textOptInStatus === TextOptInStatus.OptedIn}
                                            errorClassName={css.checkboxError}
                                            onChange={() => {
                                                if (values.textOptInStatus === TextOptInStatus.OptedIn) {
                                                    setFieldValue(nameof<CustomerDetailsForm>('textOptInStatus'), TextOptInStatus.NotDecided);
                                                }
                                                else {
                                                    setFieldValue(nameof<CustomerDetailsForm>('textOptInStatus'), TextOptInStatus.OptedIn);
                                                }
                                            }}
                                            required={values.contactPreference === 'text'}
                                        />
                                    </GMFormControl>
                                    <LegalDocumentLinks className={css.legalLinks} />
                                </GMGrid>
                            </GMGrid>
                        ) : null}
                        <GMGrid
                            id={'customer-details-contact-button-form-container'}
                            container
                            rowGap={1}
                            className={css.contactButtonList}
                        >
                            <GMGrid
                                id={'customer-details-contact-button-container'}
                                container
                                xs={12}
                                gap={isKiosk ? 3 : 1.5}
                                className={classNames(css.contactButtons, customerTextingEnabled ? css.threeButtons : css.twoButtons)}
                            >
                                <GMGrid id={'customer-details-phone-button-grid'} xs={customerTextingEnabled ? 4 : 6}>
                                    <GMContactPreferenceButton
                                        name={nameof<CustomerDetailsForm>('contactPreference')}
                                        contactPreference={ContactPreferenceEnum.Email}
                                        buttonSizeClassName={css.contactButton}
                                    />
                                </GMGrid>
                                <GMGrid id={'customer-details-email-button-grid'} xs={customerTextingEnabled ? 4 : 6}>
                                    <GMContactPreferenceButton
                                        name={nameof<CustomerDetailsForm>('contactPreference')}
                                        contactPreference={ContactPreferenceEnum.Phone}
                                        buttonSizeClassName={css.contactButton}
                                    />
                                </GMGrid>
                                {customerTextingEnabled ? (
                                    <GMGrid id={'customer-details-text-button-grid'} xs={4}>
                                        <GMContactPreferenceButton
                                            name={nameof<CustomerDetailsForm>('contactPreference')}
                                            contactPreference={ContactPreferenceEnum.Text}
                                            buttonSizeClassName={css.contactButton}
                                            onClick={async () => {
                                                await setFieldValue(nameof<CustomerDetailsForm>('contactPreference'), ContactPreferenceEnum.Text);

                                                if (optInStatus && optInStatus.isDeliverable === false && customerTextingActive) {
                                                    setShowNotDeliverableDialog(true);
                                                }
                                                else if (
                                                    values.textOptInStatus === TextOptInStatus.NotDecided
                                                    && optInStatus
                                                    && optInStatus.isDeliverable !== false
                                                    && customerTextingActive
                                                ) {
                                                    setHasClickedToProceed(true);

                                                    resetForm({
                                                        values: {
                                                            ...values,
                                                            contactPreference: undefined,
                                                        },
                                                        errors: {
                                                            textOptInStatus: errors.textOptInStatus ? errors.textOptInStatus + 1 : 'textOptInStatus is invalid',
                                                        },
                                                    });
                                                }
                                                else if (
                                                    values.textOptInStatus === TextOptInStatus.OptedOut
                                                    && optInStatus
                                                    && optInStatus.isDeliverable !== false
                                                    && customerTextingActive
                                                ) {
                                                    const { data } = await checkPhoneTextingStatus(optInStatus.phone);

                                                    await setFieldValue(nameof<CustomerDetailsForm>('textOptInStatus'), data.status);
                                                    setOptInStatus(data);

                                                    if (data.isDeliverable !== false && data.status === TextOptInStatus.OptedOut) {
                                                        setShowOptOutDialog(true);
                                                    }
                                                    else {
                                                        submitForm();
                                                    }
                                                }
                                                else {
                                                    submitForm();
                                                }
                                            }}
                                        />
                                    </GMGrid>
                                ) : null}
                            </GMGrid>
                            {submitCount !== 0 && errors.contactPreference && values.textOptInStatus !== TextOptInStatus.OptedOut ? (
                                <GMGrid
                                    id={'customer-details-contact-button-error-container'}
                                    container
                                    xs={12}
                                    className={css.error}
                                >
                                    <GMGrid id={'customer-details-contact-button-error-grid'} xs={12}>
                                        <GMErrorMessage error>
                                            {errors.contactPreference}
                                        </GMErrorMessage>
                                    </GMGrid>
                                </GMGrid>
                            ) : null}
                        </GMGrid>
                        <GMDialogInfo
                            ariaLabel={Localization.WeMissYou}
                            ariaDescribedBy={'customer-details-' + Localization.WeMissYou + '-container'}
                            className={css.dialogTitle}
                            paperDialogClasses={css.dialog}
                            title={Localization.WeMissYou}
                            onClose={async () => {
                                await resetForm({
                                    values: {
                                        ...values,
                                        contactPreference: undefined,
                                    },
                                });

                                setShowOptOutDialog(false);
                            }}
                            closeButtonText={Localization.OK}
                            open={showOptOutDialog}
                        >
                            <GMGrid container id={'customer-details-' + Localization.WeMissYou + '-container'}>
                                <GMGrid
                                    id={'customer-details-' + Localization.OhNoItLooksLikeYouOptedOutOfTextMessaging + '-grid'}
                                    className={css.dialogBody}
                                    xs={12}
                                >
                                    {Localization.OhNoItLooksLikeYouOptedOutOfTextMessaging}
                                </GMGrid>
                                <GMGrid
                                    id={'customer-details-' + Localization.TextStartTo + '-grid'}
                                    className={css.dialogBody}
                                    xs={12}
                                >
                                    {Localization.formatString(Localization.TextStartTo, textingPhone ? formatAsPhoneNumber(textingPhone) : Localization.PhoneNumber)}
                                </GMGrid>
                            </GMGrid>
                        </GMDialogInfo>
                        <GMDialogInfo
                            ariaLabel={Localization.PhoneNumberForTextMessages}
                            ariaDescribedBy={'customer-details-' + Localization.PhoneNumberForTextMessages}
                            className={css.dialogTitle}
                            paperDialogClasses={css.dialog}
                            title={Localization.PhoneNumberForTextMessages}
                            onClose={async () => {
                                await resetForm({
                                    values: {
                                        ...values,
                                        contactPreference: undefined,
                                    },
                                });

                                setShowNotDeliverableDialog(false);
                            }}
                            closeButtonText={Localization.OK}
                            open={showNotDeliverableDialog}
                        >
                            <GMGrid container id={'customer-details-' + Localization.PhoneNumberForTextMessages + '-container'}>
                                <GMGrid
                                    id={'customer-details-' + Localization.OhNoItLooksLikeYourPhoneNumberIsNotDeliverable + '-grid'}
                                    className={css.dialogBody}
                                    xs={12}
                                >
                                    {Localization.OhNoItLooksLikeYourPhoneNumberIsNotDeliverable}
                                </GMGrid>
                                <GMGrid
                                    id={'customer-details-' + Localization.PleaseEnterADifferentPhoneNumber + '-grid'}
                                    className={css.dialogBody}
                                    xs={12}
                                >
                                    {Localization.PleaseEnterADifferentPhoneNumber}
                                </GMGrid>
                            </GMGrid>
                        </GMDialogInfo>
                    </GMForm>
                );
            }}
        </Formik >
    );
};

export default CustomerDetails;